/* @flow */

import * as React from 'react';
import { useCallback } from 'react';

import type { BasicFunction } from '../../../../helpers/jsHelpers/types';

import Localizer from '../../../../helpers/localization';
import './Volume.css';
import { PictoVolume, PictoVolumeDown, PictoVolumeMute, PictoVolumeUp } from '../../../pictos/Element';
import Slider from '../../../slider/Slider';
import { VOLUME_STEP } from '../../constantsAndTypes';

const VOLUME_MIDDLE_VALUE = 0.5;

type PlayerVolumePropType = {
  +isDisabled: boolean,
  +isMuted: boolean,
  +onVolumeClickCallback?: BasicFunction,
  +onVolumeControllerClickCallback?: (volume: number) => void,
  +volume: number,
};

const PlayerVolume = ({ isDisabled, isMuted, onVolumeClickCallback, onVolumeControllerClickCallback, volume }: PlayerVolumePropType): React.Node => {
  const handleVolumeOnChange = useCallback(
    (newVolume: number) => {
      if (isDisabled || !onVolumeControllerClickCallback) {
        return;
      }

      onVolumeControllerClickCallback(newVolume);
    },
    [isDisabled, onVolumeControllerClickCallback],
  );

  const handleVolumeOnClick = useCallback(() => {
    if (isDisabled || !onVolumeClickCallback) {
      return;
    }

    onVolumeClickCallback();
  }, [isDisabled, onVolumeClickCallback]);

  let pictoVolumeDown = null;
  let pictoVolumeUp = null;
  let pictoVolumeMuted = null;

  if (!isMuted) {
    if (volume > 0 && volume < VOLUME_MIDDLE_VALUE) {
      // Display picto with 1 sound wave
      pictoVolumeDown = <PictoVolumeDown aria-hidden className='pictoVolumeDown' forceHoverEffect isDisabled={isDisabled} />;
    } else if (volume >= VOLUME_MIDDLE_VALUE) {
      // Display picto with 2 sound waves
      pictoVolumeUp = <PictoVolumeUp aria-hidden className='pictoVolumeUp' forceHoverEffect isDisabled={isDisabled} />;
    }
  } else {
    // Display picto with cross
    pictoVolumeMuted = <PictoVolumeMute aria-hidden className='pictoVolumeMuted' forceHoverEffect isDisabled={isDisabled} />;
  }

  return (
    <div className='playerVolumeContainer'>
      {pictoVolumeDown}
      {pictoVolumeUp}
      {pictoVolumeMuted}
      <PictoVolume aria-label={Localizer.localize('accessibility.player.toggle_volume')} isDisabled={isDisabled} onClick={handleVolumeOnClick} />
      <Slider ariaLabel={Localizer.localize('accessibility.player.volume_level')} id='volume-slider' max={1} min={0} onChange={handleVolumeOnChange} step={VOLUME_STEP} value={volume} />
    </div>
  );
};

export default PlayerVolume;
